

























import { first, isNil, isString } from 'lodash';
import { Component, Prop, Vue } from 'vue-property-decorator';

import { GRANULARITIES } from '@cubejs-client/vue';

import DxDropDownBox from 'devextreme-vue/drop-down-box';
import DxTreeView from 'devextreme-vue/tree-view';
import BaseMixin from '@/components/mixins/base';
import {
	TCubeDimension
} from '@cubejs-client/core';

import {
	DxMultipleComponentItemType
} from '@/container';
import moment from 'moment';

@Component({
	components: {
		DxDropDownBox, DxTreeView
	}
})
export default class TimeDimensionSelection extends BaseMixin {

	@Prop({ type: Array, default: () => [] }) timeDimensions!: any[];
	@Prop({ type: Array, required: true }) availableTimeDimensions!: TCubeDimension[];
	@Prop({ type: Function, default: () => void 0 }) setTimeDimensions!: (dim: any[]) => void;
	@Prop({ type: Boolean, default: false }) useWeek!: boolean | null;


	isTreeBoxOpened = false;
	internalTimeDimensionIds: string[] = [];
	granularities: IGranularity[] = GRANULARITIES;
	isFirstSetUp = false;

	/**
	 * https://cube.dev/docs/query-format#time-dimensions-format
	 */
	get dateRanges() {
		return ([{
			name: 'today',
			value: 0,
			kind: 'days'
		}, {
			name: 'yesterday',
			value: -1,
			kind: 'days'
		}, {
			name: 'lastWeek',
			value: -7,
			kind: 'days'
		}, {
			name: 'lastMonth',
			value: -1,
			kind: 'months'
		}, {
			name: 'lastYear',
			value: -1,
			kind: 'years'
		}, {
			name: 'next2Month',
			value: +2,
			kind: 'months'
		}, {
			name: 'nextYear',
			value: +1,
			kind: 'years'
		},
		{
			name: 'lastMonthAndNextMonth',
			value: +30,
			kind: 'months'
		},
		{
			name: 'last2MonthAndNext2Month',
			value: +31,
			kind: 'months'
		},
		{
			name: 'last6MonthAndNext6Month',
			value: +32,
			kind: 'months'
		},
		{
			name: 'lastYearAndNextYear',
			value: +33,
			kind: 'years'
		}

		] as Array<{
			name: string,
			value: number,
			kind: 'days' | 'weeks' | 'months' | 'quarter' | 'years'
		}>).map(dt => {
			let filter = `from ${-1 * dt.value} ${dt.kind} ago to now`;
			if (dt.value > 0 && dt.value < 15)
				filter = `from now to ${dt.value} ${dt.kind} from now`;
			else if (dt.value === 0)
				filter = 'today';
			else if (dt.name === 'lastMonthAndNextMonth') {
				filter = `from ${(moment().subtract(1, 'month').format('MM-DD-YYYY'))} to ${(moment().add(1, 'month').format('MM-DD-YYYY'))}`;
			}
			else if (dt.name === 'last2MonthAndNext2Month') {
				filter = `from ${(moment().subtract(2, 'month').format('MM-DD-YYYY'))} to ${(moment().add(2, 'month').format('MM-DD-YYYY'))}`;
			}
			else if (dt.name === 'last6MonthAndNext6Month') {
				filter = `from ${(moment().subtract(6, 'month').format('MM-DD-YYYY'))} to ${(moment().add(6, 'month').format('MM-DD-YYYY'))}`;
			}
			else if (dt.name === 'lastYearAndNextYear') {
				filter = `from ${(moment().subtract(1, 'year').format('MM-DD-YYYY'))} to ${(moment().add(1, 'year').format('MM-DD-YYYY'))}`;
			}
			return {
				name: dt.name,
				filter
			};
		});
	}

	created() {
		// needed otherwise it won't select the timeDimenesion inside the drop-down-box
		this.$nextTick(() => {
			const timeDimensions = this.timeDimensions;
			if (Array.isArray(timeDimensions) && timeDimensions.length > 0) {
				this.isFirstSetUp = true;
				const data = first(timeDimensions);
				let id = `${data.dimension.name}_${data.granularity}`;
				if (isString(data.dateRange))
					id = `${id}_${data.dateRange}`;
				this.internalTimeDimensionIds = [id];
				(this.$refs['treeView'] as any).instance.selectItem(id);
			}
		});
	}

	get treeViewData() {
		return this.availableTimeDimensions.flatMap(td => {
			const result: ITreeViewItem[] = this.granularities.flatMap(gr => {
				const treeViewGranularityCaption = `granularity.${gr.title}`;
				const r: ITreeViewItem[] = this.dateRanges.flatMap(dr => ({
					id: `${td.name}_${gr.name}_${dr.filter}`,
					categoryId: `${td.name}_${gr.name}`,
					dimension: td,
					dateRange: dr,
					dxDropDownBox: {
						captionCode: [td.name, treeViewGranularityCaption, `dateRange.${dr.name}`]
					},
					dxTreeView: {
						captionCode: `dateRange.${dr.name}`
					},
					granularity: gr,
				}));
				r.unshift({
					id: `${td.name}_${gr.name}`,
					categoryId: td.name,
					dimension: td,
					dxDropDownBox: {
						captionCode: [td.name, treeViewGranularityCaption]
					},
					dxTreeView: {
						captionCode: treeViewGranularityCaption
					},
					granularity: gr
				});

				return r;
			});
			result.unshift({
				id: td.name,
				dxDropDownBox: {
					captionCode: td.name
				},
				dxTreeView: {
					captionCode: td.name
				},
				dimension: td
			});
			return result;
		});
	}

	onItemSelectionChanged({ itemData }: { itemData: ITreeViewItem }) {
		if (this.isFirstSetUp) {
			this.isFirstSetUp = false;
			return;
		}
		if (!isNil(itemData.granularity)) {
			this.$emit('granularityPassed', itemData.granularity.name);
			this.internalTimeDimensionIds = [itemData.id];

			const timeDimension: any = {
				dimension: itemData.dimension.name,
				granularity: (itemData.granularity.name === 'week' ? 'day' : itemData.granularity.name) || 'day',
			};
			if (!isNil(itemData.dateRange))
				timeDimension.dateRange = itemData.dateRange.filter;

			this.setTimeDimensions([timeDimension]);
		}
	}

	onTreeItemClick({ itemData }: { itemData: ITreeViewItem }) {
		if (!isNil(itemData.granularity))
			this.isTreeBoxOpened = false;
	}

	onDropDownBoxValueChanged({ previousValue, value }: {
		previousValue: string[] | null,
		value: string[] | null
	}) {
		if (isNil(value)) {
			(this.$refs['treeView'] as any).instance.unselectAll();
			this.$emit('granularityPassed', ' ');
			this.setTimeDimensions([]);
		}
	}
}

interface ITreeViewItem extends DxMultipleComponentItemType {
	id: string;
	dimension: TCubeDimension;
	categoryId?: string;
	dateRange?: IDateRange;
	granularity?: IGranularity;
}

interface IDateRange {
	name: string;
	filter: string;
}

interface IGranularity {
	name?: string;
	title: string;
	byWeek: boolean;
}

